import {
  arrayUnion,
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  setDoc,
  type Firestore,
  type WithFieldValue,
} from "firebase/firestore";

type CreateItemDeps = {
  db: Firestore;
  currentUserString: () => string | null;
  ensureProductTypeInDb: (id: string, label?: string) => Promise<void>;
};

type CreateItemInput = {
  draftItemId: string | null;
  form: any;
  aiSuggestions: any;
  rpCustomerOpts: Array<{ key: string; name: string }>;
  steps: Record<string, any>;
  splitSerialParts: (s: string) => { base: string; visit: number };
  normalizeSerialKey: (s: string) => string;
  buildDisplaySerial: (base: string, visit: number) => string;
};

type CreateItemResult = { itemId: string; finalSerial: string };

export async function createItemWithSerialClaim(
  { db, currentUserString, ensureProductTypeInDb }: CreateItemDeps,
  {
    draftItemId,
    form,
    aiSuggestions,
    rpCustomerOpts,
    steps,
    splitSerialParts,
    normalizeSerialKey,
    buildDisplaySerial,
  }: CreateItemInput
): Promise<CreateItemResult> {
  // Bas & nyckel för index — ignorera ev. manuellt *N i input
  const { base: baseSerial } = splitSerialParts(String(form.serial || ""));
  const baseKey = normalizeSerialKey(baseSerial);
  if (!baseKey) throw new Error("Serienummer saknas eller kan inte normaliseras.");

  const rawType = String(form.productType || "").trim();
  const toSlug = (s: string) =>
    s
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9._ -]+/g, " ")
      .replace(/[\s.]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  const productTypeLabel: string = rawType;
  const productTypeId: string = String(form.productTypeId || "").trim() || (rawType ? toSlug(rawType) : "");

  // Firestore kan köra om en transaktionscallback vid konflikt. Håll därför
  // externa anrop utanför callbacken så att de inte upprepas av en retry.
  if (productTypeId) {
    try {
      await ensureProductTypeInDb(productTypeId, productTypeLabel);
    } catch (e) {
      console.warn("ensureProductTypeInDb failed:", e);
    }
  }

  const { itemRef, finalSerial } = await runTransaction(db, async (tx) => {
    const newItemRef = draftItemId
      ? doc(db, "itInventory", draftItemId)
      : doc(collection(db, "itInventory"));

    // Samma draft kan nå hit mer än en gång, exempelvis vid dubbelklick eller
    // nätverksretry. En redan finaliserad draft är samma retur, inte en ny visit.
    if (draftItemId) {
      const itemSnap = await tx.get(newItemRef);
      if (itemSnap.exists()) {
        const existing = itemSnap.data() as any;
        if (existing.isDraft === false) {
          const existingBaseKey = String(existing.serialBaseKey || "");
          const existingVisit = Number(existing.serialVisit || 0);
          const existingSerial = String(existing.serial || "");

          if (existingBaseKey === baseKey && existingVisit > 0 && existingSerial) {
            return { itemRef: newItemRef, finalSerial: existingSerial };
          }

          throw new Error("Draften är redan slutregistrerad med ett annat serienummer.");
        }
      }
    }

    const indexRef = doc(collection(db, "serialIndex"), baseKey);
    const indexSnap = await tx.get(indexRef);

    const prevVisits = indexSnap.exists() ? Number(((indexSnap.data() as any).visits || 0)) : 0;
    const prevActive = indexSnap.exists() ? Number(((indexSnap.data() as any).active || 0)) : 0;

    // Claim:a första lediga visit för denna serialBase
    let visitCandidate = prevVisits + 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const claimRef = doc(db, "serialIndex", baseKey, "claims", String(visitCandidate));
      const claimSnap = await tx.get(claimRef);
      if (!claimSnap.exists()) {
        tx.set(claimRef, {
          itemId: newItemRef.id,
          at: serverTimestamp(),
          by: currentUserString(),
        });
        break;
      }
      visitCandidate++;
    }

    const nextVisit = visitCandidate;
    const nextActive = prevActive + 1;
    const rawBase = splitSerialParts(String(form.serial || "")).base;
    const final = buildDisplaySerial(normalizeSerialKey(rawBase), nextVisit);

    const aiAccepted = {
      manufacturer:
        !!aiSuggestions?.manufacturer &&
        String(form.manufacturer || "") === String(aiSuggestions.manufacturer || ""),
      model:
        !!aiSuggestions?.model &&
        String(form.model || "") === String(aiSuggestions.model || ""),
      productType:
        !!aiSuggestions?.productType &&
        String(form.productType || "") === String(aiSuggestions.productType || ""),
    };

    const ai_corrected_fields: string[] = [];
    if (aiSuggestions?.manufacturer && form.manufacturer !== aiSuggestions.manufacturer) ai_corrected_fields.push("manufacturer");
    if (aiSuggestions?.model && form.model !== aiSuggestions.model) ai_corrected_fields.push("model");
    if (aiSuggestions?.productType && form.productType !== aiSuggestions.productType) ai_corrected_fields.push("productType");

    const cid: string =
      (form as any).customerKey ??
      (
        Array.isArray(rpCustomerOpts)
          ? rpCustomerOpts.find((c: { key: string; name: string }) => String(c.name) === String(form.customer))?.key
          : ""
      ) ??
      "";

    tx.set(newItemRef, {
      orderNumber: form.orderNumber || "",
      manufacturer: form.manufacturer,
      model: form.model,
      productTypeId,
      productType: form.productType || "",
      warrantyStartDate: form.warrantyStartDate || null,
      isDraft: false,
      wizardStep: null,
      serial: final,
      serialBase: baseSerial,
      serialBaseKey: baseKey,
      serialVisit: nextVisit,
      adapterYesNo: form.adapterYesNo || (form.chargerIncluded ? "Yes" : "No"),
      chargerIncluded: form.chargerIncluded,
      damageNotes: form.damageNotes,
      photos: {},
      createdAt: serverTimestamp(),
      createdBy: currentUserString(),
      lockedBy: null,
      lockedAt: null,
      deletePending: false,
      deleteMarkedBy: null,
      deleteMarkedAt: null,
      customer: form.customer,
      customerId: form.customerKey || cid || "",
      ai_suggestion_manufacturer: aiSuggestions?.manufacturer ?? null,
      ai_suggestion_model: aiSuggestions?.model ?? null,
      ai_suggestion_productType: aiSuggestions?.productType ?? null,
      ai_suggestion_used: !!(aiSuggestions?.manufacturer || aiSuggestions?.model || aiSuggestions?.productType),
      ai_accepted_manufacturer: aiAccepted.manufacturer,
      ai_accepted_model: aiAccepted.model,
      ai_accepted_productType: aiAccepted.productType,
      ai_corrected_fields,
      articleNumber: form.articleNumber,
      reuse: !!form.reuse,
      resold: !!form.resold,
      scrap: !!form.scrap,
      salesBatchId: form.salesBatchId || "",
      salesBatchName: form.salesBatchName || "",
      salesKolliId: form.salesKolliId || "",
      salesKolliName: form.salesKolliName || "",
      salesCpu: form.salesCpu || "",
      salesStorage: form.salesStorage || "",
      salesRam: form.salesRam || "",
      ...steps,
      auditLog: [
        { action: "created", by: currentUserString(), at: new Date().toISOString() },
      ],
      completed: false,
      completedAt: null,
      completedBy: null,
      stateSort: 0,
    } as WithFieldValue<any>);

    tx.set(
      indexRef,
      {
        lastVisit: nextVisit,
        visits: nextVisit,
        active: nextActive,
        lastItemId: newItemRef.id,
        lastSeen: serverTimestamp(),
      },
      { merge: true }
    );

    return { itemRef: newItemRef, finalSerial: final };
  });

  return { itemId: itemRef.id, finalSerial };
}
